import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface GalleryImage {
  id: string;
  url: string;
  alt: string;
}

export interface ApartmentWithTranslation {
  id: string;
  number: number;
  house: string;
  houseName: string;
  name: string;
  shortDescription: string;
  description: string;
  features: string[];
  maxGuests: number;
  rooms: number;
  size: string;
  image: string;
  galleryImages: GalleryImage[];
  isPublished: boolean;
  traumListingId: string;
  specialFeatures: string;
}

async function fetchApartments(house?: string, lang = "de"): Promise<ApartmentWithTranslation[]> {
  let query = supabase
    .from("apartments")
    .select(`
      id, number, house, house_name, features, max_guests, rooms, size, sort_order, is_published, traum_listing_id, special_features, main_image, short_description,
      apartment_translations(name, description, language_code),
      apartment_images(media_id, is_primary, sort_order, media!inner(url, alt_text))
    `)
    .eq("is_published", true)
    .order("sort_order", { ascending: true });

  if (house) {
    query = query.eq("house", house);
  }

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((apt: any) => {
    const translations = apt.apartment_translations ?? [];
    const t = translations.find((tr: any) => tr.language_code === lang) || translations[0];
    const allImages = (apt.apartment_images ?? []).sort((a: any, b: any) => a.sort_order - b.sort_order);
    const primaryImg = allImages.find((img: any) => img.is_primary);
    const firstImg = allImages[0];
    const img = primaryImg || firstImg;

    return {
      id: apt.id,
      number: apt.number,
      house: apt.house,
      houseName: apt.house_name,
      name: t?.name ?? `Ferienwohnung ${apt.number}`,
      shortDescription: apt.short_description ?? "",
      description: t?.description ?? "",
      features: apt.features ?? [],
      maxGuests: apt.max_guests,
      rooms: apt.rooms,
      size: apt.size ?? "",
      image: apt.main_image ?? img?.media?.url ?? "",
      galleryImages: allImages
        .filter((i: any) => !i.is_primary && i.media?.url)
        .map((i: any) => ({ id: i.media_id, url: i.media.url, alt: i.media.alt_text || "" })),
      isPublished: apt.is_published,
      traumListingId: apt.traum_listing_id ?? "",
      specialFeatures: apt.special_features ?? "",
    };
  });
}

export function useApartments(house?: string, lang = "de") {
  return useQuery({
    queryKey: ["apartments", house, lang],
    queryFn: () => fetchApartments(house, lang),
    staleTime: 0,
    gcTime: 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

export function useApartment(id?: string, lang = "de") {
  return useQuery({
    queryKey: ["apartment", id, lang],
    queryFn: async (): Promise<ApartmentWithTranslation | null> => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("apartments")
        .select(`
          id, number, house, house_name, features, max_guests, rooms, size, is_published, traum_listing_id, special_features, main_image, short_description,
          apartment_translations(name, description, language_code),
          apartment_images(media_id, is_primary, sort_order, media!inner(url, alt_text))
        `)
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const translations = (data as any).apartment_translations ?? [];
      const t = translations.find((tr: any) => tr.language_code === lang) || translations[0];
      const imgs = (data as any).apartment_images ?? [];
      const allImages = imgs.sort((a: any, b: any) => a.sort_order - b.sort_order);
      const primaryImg = allImages.find((img: any) => img.is_primary);
      const firstImg = allImages[0];
      const img = primaryImg || firstImg;

      return {
        id: data.id,
        number: data.number,
        house: data.house,
        houseName: data.house_name,
        name: t?.name ?? `Ferienwohnung ${data.number}`,
        shortDescription: (data as any).short_description ?? "",
        description: t?.description ?? "",
        features: data.features ?? [],
        maxGuests: data.max_guests,
        rooms: data.rooms,
        size: data.size ?? "",
        image: (data as any).main_image ?? img?.media?.url ?? "",
        galleryImages: allImages
          .filter((i: any) => !i.is_primary && i.media?.url)
          .map((i: any) => ({ id: i.media_id, url: i.media.url, alt: i.media.alt_text || "" })),
        isPublished: data.is_published,
        traumListingId: (data as any).traum_listing_id ?? "",
        specialFeatures: (data as any).special_features ?? "",
      };
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}
